/**
 * api.js — API client for the kick-viz-express backend.
 *
 * All HTTP communication with the backend goes through this module. It uses
 * JWT token authentication (Bearer token in the Authorization header). The
 * token is persisted in localStorage under the key `kickviz_jwt_token`.
 *
 * Exported API objects (grouped by resource):
 * - authAPI      — register, login, session, logout, forgot/reset password
 * - accountsAPI  — CRUD for accounts + nested teams/scenarios
 * - teamsAPI     — get/update/delete teams
 * - scenariosAPI — get/update/delete scenarios
 * - stripeAPI    — checkout, portal, subscription status
 * - uploadsAPI   — team logo + player photo uploads (multipart/form-data)
 * - feedbackAPI  — submit user feedback
 *
 * Exported helpers:
 * - dbScenarioToMenuItem — convert a backend scenario to the app's internal format
 * - dbTeamToTeamInfo     — convert a backend team to the app's internal format
 * - getMediaUrl          — resolve a backend media path to a full URL
 * - getTeamLogoUrl       — resolve a team's logo URL (or placeholder)
 * - getPlayerPhotoUrl    — resolve a player's photo URL (or placeholder)
 */
import { getRuntimeAdapter, isMobileBuild } from './platform';
const BASE_URL = process.env.REACT_APP_API_URL || '/api';
export const HELP_URL = process.env.REACT_APP_HELP_URL || 'https://help.kickviz.com/tutorials-v3';
const TOKEN_KEY = 'kickviz_jwt_token';

// ── Token management ────────────────────────────────

/** Read the JWT token from localStorage. */
export function getToken() {
  if (isMobileBuild) return getRuntimeAdapter() ? 'device-workspace' : null;
  return localStorage.getItem(TOKEN_KEY);
}

/** Persist the JWT token to localStorage. */
export function setToken(token) {
  if (isMobileBuild) return; // Native credentials are kept in the OS secure store.
  localStorage.setItem(TOKEN_KEY, token);
}

/** Remove the JWT token from localStorage (used on logout). */
export function clearToken() {
  if (isMobileBuild) return;
  localStorage.removeItem(TOKEN_KEY);
}

// ── Generic fetch helper ────────────────────────────

/**
 * Perform an authenticated HTTP request to the backend API.
 *
 * Automatically attaches the JWT token (if present) and serialises the body
 * as JSON (or passes FormData through untouched when `isFormData` is true).
 * Includes `credentials: 'include'` so the httpOnly `jwt` cookie is sent and
 * received cross-origin — this keeps the React visualizer and backend EJS
 * pages in sync.
 *
 * @param {string} method            - HTTP method ('GET', 'POST', 'PUT', 'DELETE').
 * @param {string} path              - API path appended to BASE_URL (e.g. '/teams/123').
 * @param {Object|FormData} [body]   - Request body (serialised to JSON unless FormData).
 * @param {boolean} [isFormData]     - If true, send body as multipart/form-data.
 * @returns {Promise<Object>} Parsed JSON response.
 * @throws {Error} If the response status is not ok (includes server error message).
 */
async function request(method, path, body = null, isFormData = false) {
  if (isMobileBuild) {
    const runtime = getRuntimeAdapter();
    if (!runtime) throw new Error('Open the visualizer from the mobile app.');
    await runtime.ready;
    return runtime.request(method, path, body, isFormData);
  }
  const token = getToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (!isFormData) headers['Content-Type'] = 'application/json';

  const opts = { method, headers, credentials: 'include' };
  if (body) {
    opts.body = isFormData ? body : JSON.stringify(body);
  }

  const res = await fetch(`${BASE_URL}${path}`, opts);
  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return data;
}

// ── Auth ────────────────────────────────────────────

export const authAPI = {
  /** Register a new account; stores the returned JWT token. */
  register: async (name, email, password) => {
    const data = await request('POST', '/auth/register', { name, email, password });
    setToken(data.token);
    return data;
  },
  /** Log in with email + password; stores the returned JWT token. */
  login: async (email, password) => {
    const data = await request('POST', '/auth/login', { email, password });
    setToken(data.token);
    return data;
  },
  /** Fetch the current session (account + teams + scenarios) using the stored token. */
  session: () => request('GET', '/auth/session'),
  /**
   * Log out — calls the backend to clear the httpOnly cookie, then removes
   * the localStorage token. This keeps both auth stores in sync.
   */
  logout: async () => {
    try { await request('POST', '/auth/logout'); } catch (e) { /* ignore — cookie may already be gone */ }
    clearToken();
  },
  /**
   * Check if a valid jwt cookie session exists on the backend.
   * If yes, sync it to localStorage and return the token + account.
   * If no, return null. Used on app startup and window focus to detect
   * logins/logouts that happened via the backend EJS pages.
   */
  checkCookie: async () => {
    try {
      const data = await request('GET', '/auth/check-cookie');
      if (data.token) setToken(data.token);
      return data;
    } catch (e) {
      return null;
    }
  },
  /** Request a password-reset email. */
  forgotPassword: (email) => request('POST', '/auth/forgot-password', { email }),
  /** Reset password using a token from the reset email; stores the new JWT. */
  resetPassword: (token, password) => {
    return request('POST', '/auth/reset-password', { token, password }).then(data => {
      setToken(data.token);
      return data;
    });
  },
  /** Change the display name (JSON twin of the /account page form). */
  changeName: (name) => request('POST', '/account/name', { name }),
  /** Change password in-place; stores the re-issued JWT. */
  changePassword: async (currentPassword, newPassword) => {
    const data = await request('POST', '/account/password', { currentPassword, newPassword });
    if (data.token) setToken(data.token);
    return data;
  },
  /**
   * Request account deletion (grace-period flow server-side). Needs the
   * current password + the exact confirmation phrase the backend expects.
   */
  requestDeletion: (password, confirmation) =>
    request('POST', '/account/deletion-request', { password, confirmation }),
};

// ── Accounts ────────────────────────────────────────

// ── Public config ────────────────────────────────────

/** Fetch public app configuration (ad settings etc). No auth required. */
export const configAPI = {
  get: () => request('GET', '/config'),
};

export const accountsAPI = {
  list: () => request('GET', '/accounts'),
  get: (id) => request('GET', `/accounts/${id}`),
  create: (name, email = '') => request('POST', '/accounts', { name, email }),
  update: (id, { name, email }) => request('PUT', `/accounts/${id}`, { name, email }),
  delete: (id) => request('DELETE', `/accounts/${id}`),
  teams: (id) => request('GET', `/accounts/${id}/teams`),
  createTeam: (id, team) => request('POST', `/accounts/${id}/teams`, team),
  scenarios: (id) => request('GET', `/accounts/${id}/scenarios`),
  createScenario: (id, scenario) => request('POST', `/accounts/${id}/scenarios`, scenario),
};

// ── Teams ───────────────────────────────────────────

export const teamsAPI = {
  /** Get default home/away teams (public, no auth needed). */
  getDefaults: () => request('GET', '/teams/defaults'),
  get: (id) => request('GET', `/teams/${id}`),
  update: (id, data) => request('PUT', `/teams/${id}`, data),
  delete: (id) => request('DELETE', `/teams/${id}`),
};

// ── Scenarios ───────────────────────────────────────

export const scenariosAPI = {
  get: (id) => request('GET', `/scenarios/${id}`),
  update: (id, data) => request('PUT', `/scenarios/${id}`, data),
  delete: (id) => request('DELETE', `/scenarios/${id}`),
  /** Search public scenarios (premium only). Returns paginated results. */
  searchPublic: (q = '', page = 1) => request('GET', `/scenarios/search?q=${encodeURIComponent(q)}&page=${page}`),
  /** Search open scenarios (no auth required, curated accounts only). */
  searchOpen: (q = '', page = 1) => request('GET', `/scenarios/open-search?q=${encodeURIComponent(q)}&page=${page}`),
  /** Get a single open scenario (no auth required, curated accounts only). */
  getOpen: (id) => request('GET', `/scenarios/open/${id}`),
  /** Get the site's default scenario (no auth required, set by an admin). */
  getDefaultOpen: () => request('GET', '/scenarios/default-open'),
};

// ── Stripe ──────────────────────────────────────────

export const stripeAPI = {
  /** Start a Stripe Checkout session for the given plan ('monthly' | 'yearly'). */
  checkout: (plan) => request('POST', '/stripe/checkout', { plan }),
  /** Open the Stripe Customer Portal for subscription management. */
  portal: () => request('POST', '/stripe/portal'),
  /** Fetch the current subscription status for the logged-in account. */
  status: () => request('GET', '/stripe/status'),
};

// ── Uploads (images) ────────────────────────────────

/**
 * Resize an image file to fit within maxDim×maxDim using a canvas.
 * Returns a Promise<File>. If the image is already small enough or is not
 * an image, returns the original file unchanged.
 * @param {File} file - The image file to resize.
 * @param {number} [maxDim=1024] - Maximum width/height in pixels.
 * @returns {Promise<File>}
 */
export async function resizeImageFile(file, maxDim = 1024) {
  if (!file.type.startsWith('image/')) return file;
  try {
    const img = await new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const i = new Image();
      i.onload = () => { URL.revokeObjectURL(url); resolve(i); };
      i.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')); };
      i.src = url;
    });
    // Already small enough — return original
    if (img.width <= maxDim && img.height <= maxDim) return file;
    // Calculate scaled dimensions (preserve aspect ratio)
    const scale = Math.min(maxDim / img.width, maxDim / img.height);
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.85));
    return new File([blob], file.name.replace(/\.(png|gif|webp|svg)$/i, '.jpg'), { type: 'image/jpeg' });
  } catch {
    // If resize fails, send the original — server-side multer will handle size limits
    return file;
  }
}

export const uploadsAPI = {
  /** Upload a team logo image (auto-resized to max 1024px before upload). */
  uploadTeamLogo: async (teamId, file) => {
    const resized = await resizeImageFile(file, 512);
    const formData = new FormData();
    formData.append('logo', resized);
    return request('POST', `/uploads/team/${teamId}/logo`, formData, true);
  },
  /** Upload a player photo image (auto-resized to max 1024px before upload). */
  uploadPlayerPhoto: async (teamId, playerIndex, file) => {
    const resized = await resizeImageFile(file, 1024);
    const formData = new FormData();
    formData.append('photo', resized);
    return request('POST', `/uploads/team/${teamId}/player/${playerIndex}/photo`, formData, true);
  },
  /** Skin + hair style catalog (id + label per style) for the character editor. */
  styles: () => request('GET', '/uploads/viz-assets/styles'),
  /**
   * Upload kit textures in one multipart POST. keys are the schema names
   * (shirtFront, shirtBack, shortsFront, shortsBack, socksFront,
   * gkShirtFront, gkShirtBack, gkShortsFront, gkShortsBack, gkSocksFront).
   */
  uploadTeamTextures: async (teamId, filesByField) => {
    const FIELD_NAMES = {
      shirtFront: 'shirt_front', shirtBack: 'shirt_back',
      shortsFront: 'shorts_front', shortsBack: 'shorts_back',
      socksFront: 'socks_front',
      gkShirtFront: 'gk_shirt_front', gkShirtBack: 'gk_shirt_back',
      gkShortsFront: 'gk_shorts_front', gkShortsBack: 'gk_shorts_back',
      gkSocksFront: 'gk_socks_front',
    };
    const formData = new FormData();
    let any = false;
    for (const [key, file] of Object.entries(filesByField || {})) {
      if (file && FIELD_NAMES[key]) { formData.append(FIELD_NAMES[key], file); any = true; }
    }
    if (!any) throw new Error('No texture files provided');
    return request('POST', `/uploads/team/${teamId}/textures`, formData, true);
  },
  /** Clear one kit texture. field is the multipart name, e.g. 'shirt_front'. */
  clearTeamTexture: (teamId, field) => request('DELETE', `/uploads/team/${teamId}/textures/${field}`),
};

// ── Feedback ────────────────────────────────────────

export const feedbackAPI = {
  /** Submit a user feedback message with a category. */
  submit: (message, category) => request('POST', '/feedback', { message, category }),
};

// ── Helpers ─────────────────────────────────────────

// Backend base URL (without /api suffix) for constructing media URLs
export const BACKEND_BASE = BASE_URL.replace(/\/api\/?$/, '');

/**
 * Resolve a media path to a full URL.
 * - Absolute URLs (http…) are returned as-is.
 * - Backend upload paths (/uploads/…) are prefixed with the backend base URL.
 * - Anything else is returned unchanged.
 *
 * @param {string} [path] - A media path or null.
 * @returns {string|null} The resolved URL, or null if path is falsy.
 */
export function getMediaUrl(path) {
  if (!path) return null;
  if (isMobileBuild) return getRuntimeAdapter()?.resolveAsset(path) || path;
  if (path.startsWith('http')) return path;
  if (path.startsWith('/uploads/')) return `${BACKEND_BASE}${path}`;
  return path;
}

/**
 * Resolve a team's logo URL, falling back to the placeholder SVG.
 * @param {Object} [team] - Team object with an optional `badge` path.
 * @returns {string} The logo URL or placeholder path.
 */
export function getTeamLogoUrl(team) {
  if (team?.badge && team.badge.startsWith('/uploads/')) return getMediaUrl(team.badge);
  return '/placeholders/team-logo.svg';
}

/**
 * Resolve a player's photo URL, falling back to the placeholder SVG.
 * @param {Object} [player] - Player object with an optional `photo` path.
 * @returns {string} The photo URL or placeholder path.
 */
export function getPlayerPhotoUrl(player) {
  if (player?.photo && player.photo.startsWith('/uploads/')) return getMediaUrl(player.photo);
  return '/placeholders/player-photo.svg';
}

/**
 * Convert a backend scenario document to the app's internal scenario format.
 * Normalises tactical data without restoring team identities.
 *
 * @param {Object} dbScenario - Scenario document from the backend.
 * @returns {Object} Scenario in the app's internal format.
 */
export function dbScenarioToMenuItem(dbScenario) {
  return {
    id: dbScenario._id,
    name: dbScenario.name,
    phases: (dbScenario.phases || []).map(p => ({
      name: p.name || 'Phase 1',
      duration: p.duration ?? 3,
      start: p.start || {},
      // Legacy V2/V1 transition pose. V3 animates from keyframes, but an old
      // scenario's transition is still that scenario's own work — pass it through
      // so opening it in V3 never quietly drops it.
      ...(p.transition !== undefined ? { transition: p.transition } : {}),
    })),
    // V2 timeline tracks — CRITICAL: pass through untouched so movement keys,
    // attachments, action/graphic keys and zone visual windows survive a
    // save → load round-trip. (Mongoose keeps the raw array; strict schema
    // fields are attach/visual/position/action/graphic.)
    keyframes: dbScenario.keyframes || [],
    // V3 branch tree — pass through untouched (Mixed on the backend). Scenarios
    // saved by V2 have none, and the frontend wraps them as a lone root branch.
    tree: dbScenario.tree || null,
    timelineDuration: dbScenario.timelineDuration || 60,
    createdAt: dbScenario.createdAt,
    sceneVersion: dbScenario.sceneVersion || '1.0',
    // Share flag — drives the public/private toggle in the scenario manager.
    isPublic: !!dbScenario.isPublic,
  };
}

/**
 * Convert a backend team document to the app's internal team-info format.
 * Applies sensible defaults for missing kit colours and player fields.
 *
 * @param {Object} dbTeam - Team document from the backend.
 * @returns {Object|null} Team info object, or null if dbTeam is falsy.
 */
export function dbTeamToTeamInfo(dbTeam) {
  if (!dbTeam) return null;
  return {
    _id: dbTeam._id,
    name: dbTeam.name,
    badge: dbTeam.badge || '',
    color1: dbTeam.color1 || '#ff0000',
    color2: dbTeam.color2 || '#000000',
    color3: dbTeam.color3 || '#ff0000',
    color4: dbTeam.color4 || '#000000',
    playerInfo: (dbTeam.playerInfo || []).map(p => ({
      name: p.name || '',
      number: String(p.number || ''),
      position: p.position || '',
      photo: p.photo || '',
      skinStyle: p.skinStyle || 'style_000',
      hairStyle: p.hairStyle || 'style_000',
    })),
    shirtFront: getMediaUrl(dbTeam.shirtFront) || '',
    shirtBack: getMediaUrl(dbTeam.shirtBack) || '',
    shortsFront: getMediaUrl(dbTeam.shortsFront) || '',
    shortsBack: getMediaUrl(dbTeam.shortsBack) || '',
    socksFront: getMediaUrl(dbTeam.socksFront) || '',
    gkShirtFront: getMediaUrl(dbTeam.gkShirtFront) || '',
    gkShirtBack: getMediaUrl(dbTeam.gkShirtBack) || '',
    gkShortsFront: getMediaUrl(dbTeam.gkShortsFront) || '',
    gkShortsBack: getMediaUrl(dbTeam.gkShortsBack) || '',
    gkSocksFront: getMediaUrl(dbTeam.gkSocksFront) || '',
    numberColor: dbTeam.numberColor || '#ffffff',
  };
}

export default { authAPI, configAPI, accountsAPI, teamsAPI, scenariosAPI, stripeAPI, uploadsAPI, feedbackAPI, dbScenarioToMenuItem, dbTeamToTeamInfo, getMediaUrl, getTeamLogoUrl, getPlayerPhotoUrl };
